import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ActionCreators from '../../redux/action';
import { withRouter } from 'react-router-dom';
import LoginSC from './login.styled';
import { useEffect } from 'react';
import { LS_ACCESS_TOKEN, LS_REFRESH_TOKEN } from '../../constants';
import LoginForm from './login-form';

import Button from '../ui/button';
import { FormattedMessage } from 'react-intl';
import dlt from '../../utils/default-lang';
import { ReactComponent as WishmakerLogo } from '../../assets/img/svg/wishmaker-logo-big.svg';
import jwt from 'jsonwebtoken';
import WishmakerModal from '../ui/modal/modal';
import ResendModal from './resend-modal';

const _Login = ({ signIn, logOut, match, location, registrationCheck, sendPublicSms, verifyPhoneNumber }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResendModalOpened, setResendModalOpened] = useState(false);
  const [isBadCredentials, setBadCredentials] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  useEffect(() => {
    let accessToken = localStorage.getItem(LS_ACCESS_TOKEN);
    let refreshToken = localStorage.getItem(LS_REFRESH_TOKEN);
    const currentTime = Date.now() / 1000;
    const decoded = jwt.decode(accessToken);

    if (accessToken && refreshToken) {
      if (decoded.exp < currentTime) {
        logOut({ force: true });
      } else {
        window.history.pushState(
          null,
          null,
          `/?redirect-url=${location.pathname}#${accessToken}/${refreshToken}`
        );
        window.location.reload();
      }
    }
  }, [location.pathname, logOut]);

  const onSubmitForm = async (values) => {
    // Najprv skontroluj registračný status (vrátane phone_verified)
    setIsLoading(true);

    const checkRegistration = () => {
      return new Promise((resolve) => {
        registrationCheck({
          id: values.wm_id,
          onSuccess: (res) => {
            resolve(res);
          },
          // Account doesn't exist (404) or the check itself failed - either way,
          // don't hang forever. Fall through to the real login attempt below,
          // which is the authoritative check and already shows a proper
          // "bad credentials" message on 401.
          onError: () => {
            resolve(null);
          },
        });
      });
    };

    try {
      const regStatus = await checkRegistration();

      // Skontroluj, či je telefón overený
      const phoneVerified = regStatus?.phone_verified ?? regStatus?.phoneVerified ??
                           regStatus?.data?.phone_verified ?? regStatus?.data?.phoneVerified;

      // Ak nie je telefón overený, zastav prihlásenie
      if (regStatus?.message === 'REGISTERED' && phoneVerified === false) {
        setIsLoading(false);
        setRegistrationStatus(regStatus);
        return;
      }

      // Pokračuj s normálnym prihlásením
      const formattedData = {
        login: values.wm_id,
        password: values.password,
      };

      const onSuccess = (res) => {
        // Client-zone login now returns a single long-lived token (same as
        // backoffice, no refresh token) - reuse it for both hash segments so
        // the existing token bootstrap in index.js/here keeps working as-is.
        window.history.pushState(
          null,
          null,
          `/?redirect-url=${location.pathname}#${res.token}/${res.token}`
        );
        window.location.reload();
        setIsLoading(false);
      };

      const onBadCredentials = () => {
        setBadCredentials(true);
        setIsLoading(false);
      };

      await signIn({ data: formattedData, onSuccess, onBadCredentials });
    } catch (error) {
      setIsLoading(false);
      console.error('Login error:', error);
    }
  };

  const onForgotPasswordClick = () => {
    setResendModalOpened(true);
  };

  const handleRegistrationCheckResponse = useCallback((res) => {
    setRegistrationStatus(res);
  }, []);

  const handleValidateId = useCallback(
    async (id) => {
      setRegistrationStatus(null);
      await registrationCheck({
        id,
        onSuccess: handleRegistrationCheckResponse,
        onError: (err) => {
          if (err?.status === 404) {
            setRegistrationStatus({ message: 'NOT_FOUND' });
          }
        },
      });
    },
    [handleRegistrationCheckResponse, registrationCheck]
  );

  return (
    <>
      <WishmakerModal
        isOpened={isResendModalOpened}
        onClose={() => setResendModalOpened(false)}
        className="sm"
        withCross
      >
        <ResendModal onClose={() => setResendModalOpened(false)} />
      </WishmakerModal>
      <LoginSC>
        <div className="logo-wrap">
          <WishmakerLogo />
        </div>
        <div className="form-wrap">
          <LoginForm
            onSubmitForm={(values) => onSubmitForm(values)}
            isLoading={isLoading}
            isBadCredentials={isBadCredentials}
            handleValidateId={handleValidateId}
            registrationStatus={registrationStatus}
            handleStatusReset={() => handleRegistrationCheckResponse(null)}
            sendPublicSms={sendPublicSms}
            verifyPhoneNumber={verifyPhoneNumber}
          />
        </div>
        <Button type="LINK" className="forgot-password" onClick={() => onForgotPasswordClick()}>
          <FormattedMessage
            id="login_form_forgot_password"
            defaultMessage={dlt['login_form_forgot_password']}
            onClick={() => setResendModalOpened(true)}
          />
        </Button>
      </LoginSC>
    </>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      signIn: ActionCreators.signIn,
      logOut: ActionCreators.logOut,
      registrationCheck: ActionCreators.registrationCheck,
      sendPublicSms: ActionCreators.sendPublicSms,
      verifyPhoneNumber: ActionCreators.verifyPhoneNumber,
    },
    dispatch
  );
};

const Login = withRouter(connect(null, mapDispatchToProps)(_Login));

export default Login;
